import React, { useState, Dispatch, SetStateAction, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./Forms.module.css";
import TimeCountDown from "../UILibrary/TimeCountDown";
import Submission from "../UILibrary/Submission";
import VerificationIcon from "Utils/Assets/SVGs/VerificationIcon";
import VerificationInput from "../UILibrary/VerificationInput";
import { CodeChangePayload } from "@model/UIComponents.model";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";
import { UserRegistrationStoreImplementation } from "Data/UserRegistration/UserRegistrationStoreImplementation";
import { IVerificationParams } from "@model/Authentication.model";

const verificationCodeLength = 4;
const newRequestWaitTime = 60;

const VerificationForm = () => {
  const userRegistrationStore = UserRegistrationStoreImplementation();
  const verificationState =
    userRegistrationStore?.registrationState?.verification;

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: "onSubmit",
    defaultValues: { code: Array(verificationCodeLength).fill("") },
  });

  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [code, setCode] = useState<string[]>(
    Array(verificationCodeLength).fill("")
  );
  const [canSubmit, setCanSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verification, setVerification] = useState<IVerificationParams | null>(
    null
  );

  useEffect(() => {
    setVerification(
      userRegistrationStore?.registrationState?.verification || null
    );

    userRegistrationStore?.clearVerificationData();
    return () => {};
  }, []);

  const auth = new AuthenticationService();

  const message = !verification?.isNew
    ? "This email is already registered. Please verify your email."
    : "You have successfully registered. Please check your email for a verification code to verify your account.";

  const onCodeChanged = (payload: CodeChangePayload) => {
    setCode(payload.data);
    setCanSubmit(payload.isComplete);
    if (validationError) setValidationError(undefined);
  };

  const onSubmit = async () => {
    if (!canSubmit) {
      setValidationError(
        `Please enter all ${verificationCodeLength} characters`
      );
      setApiMessage(null);
      return;
    }

    setApiError(null);
    setApiMessage(null);

    await auth.handleVerifyCode({
      code: code.join(""),
      setApiError,
      setLoading,
    });
  };

  const requestNewCode = async (
    setActionAllowed: Dispatch<SetStateAction<boolean>>
  ) => {
    if (!verification?.email) {
      setApiError("Missing email. Please go back to log in or sign up.");
      return;
    }

    setActionAllowed(false);
    setApiMessage(null);
    setApiError(null);

    const msg = await auth.handleResendVerification({
      email: verification.email,
      isNew: verification.isNew,
      navigate: false,
      setApiError: (m) => {
        if (m) setApiMessage(null);
        setApiError(m);
      },
      setLoading,
    });

    if (msg) {
      setApiError(null);
      setApiMessage(msg);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.formContainerr}>
        <form
          className={styles.formInner}
          data-testid="verification_form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={styles.verificationIconWrapper}>
            <VerificationIcon />
          </div>

          <h2 className={styles.formTitle}>Registration Successful!</h2>
          <p className={styles.formText}>{message}</p>
          <p className={styles.formTextt} data-testid="code-email">
            <strong>Code sent to:</strong> {verificationState?.email}
          </p>

          <Controller
            name="code"
            control={control}
            rules={{
              validate: (value) =>
                value.every((digit) => digit !== "") ||
                `Please enter all ${verificationCodeLength} characters`,
            }}
            render={({ field, fieldState }) => (
              <VerificationInput
                length={verificationCodeLength}
                type="number"
                onChange={(payload) => {
                  onCodeChanged(payload);
                  field.onChange(payload.data);
                }}
                error={fieldState.error?.message || validationError}
              />
            )}
          />

          <TimeCountDown
            message="Request New Code"
            time={newRequestWaitTime}
            action={requestNewCode}
          />

          {apiMessage && (
            <div
              className={styles.successMessage}
              data-testid="reset_success_message"
            >
              <p>{apiMessage}</p>
            </div>
          )}

          <Submission
            buttonText={loading ? "Verifying..." : "Verify"}
            disabled={!isValid || !canSubmit || loading}
            error={apiError ?? undefined}
          />
        </form>
      </div>
    </div>
  );
};

export default VerificationForm;
